import { useState, useEffect, useCallback } from 'react';
import { DisconnectOutlined, SyncOutlined } from '@ant-design/icons';

const CACHE_PREFIX = 'raporgor-cache-';

function getLatestCacheTimestamp(): Date | null {
  let latest: number | null = null;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      try {
        const entry = JSON.parse(localStorage.getItem(key) || '');
        if (entry?.timestamp && (latest === null || entry.timestamp > latest)) {
          latest = entry.timestamp;
        }
      } catch { /* skip invalid entries */ }
    }
  }
  return latest ? new Date(latest) : null;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 1000 / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'az önce';
  if (diffMins < 60) return `${diffMins} dakika önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

interface AgentStatusBannerProps {
  hasError: boolean;
  onRetry?: () => void;
}

export function AgentStatusBanner({ hasError, onRetry }: AgentStatusBannerProps) {
  const [lastCacheTime, setLastCacheTime] = useState<Date | null>(null);

  const updateCacheTime = useCallback(() => {
    setLastCacheTime(getLatestCacheTimestamp());
  }, []);

  useEffect(() => {
    updateCacheTime();
  }, [updateCacheTime, hasError]);

  if (!hasError) return null;

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        <DisconnectOutlined style={styles.icon} />
        <div style={styles.text}>
          <span style={styles.title}>Agent bağlantısı kurulamadı</span>
          {lastCacheTime && (
            <span style={styles.subtitle}>
              Son veri: {formatRelativeTime(lastCacheTime)}
            </span>
          )}
        </div>
      </div>
      {onRetry && (
        <button onClick={onRetry} style={styles.retryButton}>
          <SyncOutlined style={{ marginRight: 6 }} />
          Tekrar Dene
        </button>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    background: '#fef3c7',
    border: '1px solid #f59e0b',
    borderRadius: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 8,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  icon: {
    fontSize: 18,
    color: '#b45309',
  },
  text: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    color: '#92400e',
  },
  subtitle: {
    fontSize: 11,
    color: '#a16207',
  },
  retryButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#92400e',
    background: '#fde68a',
    border: '1px solid #f59e0b',
    borderRadius: 6,
    cursor: 'pointer',
  },
};
